/**
 * 轻量 RUM（真实用户监控）采集：LCP / CLS / INP 近似值 / 长任务 / 路由切换耗时。
 *
 * 为什么不直接用 web-vitals 库：骨架阶段不想为了 5 个指标引入一个依赖，
 * 而且这里需要把「路由切换耗时」和内部导航标记打通（见 markNavStart/markRouteRendered）。
 * 生产环境可以替换为 web-vitals（本文件保留了同一个 snapshot 接口）。
 *
 * 所有 observe 调用都包 try/catch：不同浏览器支持的 entry type 不同，
 * 监控代码绝不允许把主流程搞崩。
 */

export interface RumSnapshot {
  readonly lcpMs: number | null;
  readonly cls: number;
  /** 最大事件处理耗时（INP 的近似口径，Chromium 支持 event + durationThreshold） */
  readonly inpMs: number | null;
  readonly longTaskCount: number;
  readonly routeTransitionMs: number | null;
  readonly routeTransitionLabel: string | null;
}

class RumCollector {
  private lcpMs: number | null = null;
  private cls = 0;
  private inpMs: number | null = null;
  private longTaskCount = 0;
  private routeTransitionMs: number | null = null;
  private routeTransitionLabel: string | null = null;
  private pendingNavStart: number | null = null;
  private started = false;

  start(): void {
    if (this.started || typeof window === "undefined") return;
    this.started = true;

    this.observe("largest-contentful-paint", (entry) => {
      this.lcpMs = Math.round(entry.startTime);
    });

    this.observe("layout-shift", (entry) => {
      const shift = entry as unknown as { value?: number; hadRecentInput?: boolean };
      if (shift.hadRecentInput === true) return;
      if (typeof shift.value === "number") {
        this.cls = Number((this.cls + shift.value).toFixed(4));
      }
    });

    this.observe("longtask", () => {
      this.longTaskCount += 1;
    });

    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const duration = entry.duration;
          if (this.inpMs === null || duration > this.inpMs) this.inpMs = Math.round(duration);
        }
      });
      observer.observe({
        type: "event",
        buffered: true,
        durationThreshold: 40,
      } as PerformanceObserverInit);
    } catch {
      /* 不支持 event 观察项的浏览器：INP 留空，不假装有数据 */
    }
  }

  private observe(entryType: string, handler: (entry: PerformanceEntry) => void): void {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) handler(entry);
      });
      observer.observe({ type: entryType, buffered: true });
    } catch {
      /* 静默跳过不支持的 entry type */
    }
  }

  /** 用户触发导航时调用（点击 Link / 命令面板 / 快捷键） */
  markNavStart(): void {
    this.pendingNavStart = performance.now();
  }

  /** 目标路由挂载后调用，记录「点击 → 新页面渲染完成」的耗时 */
  markRouteRendered(label: string): void {
    if (this.pendingNavStart === null) return;
    const elapsed = performance.now() - this.pendingNavStart;
    this.pendingNavStart = null;
    if (elapsed > 3000) return; // 明显不是一次导航（例如标签页被挂起），丢弃
    this.routeTransitionMs = Number(elapsed.toFixed(1));
    this.routeTransitionLabel = label;
  }

  snapshot(): RumSnapshot {
    return {
      lcpMs: this.lcpMs,
      cls: this.cls,
      inpMs: this.inpMs,
      longTaskCount: this.longTaskCount,
      routeTransitionMs: this.routeTransitionMs,
      routeTransitionLabel: this.routeTransitionLabel,
    };
  }
}

export const rum = new RumCollector();
